import { Controller } from "@hotwired/stimulus"
import PhotoSwipe from "photoswipe"
import PhotoSwipeLightbox from "photoswipe/lightbox"

import { lightboxMotion, playWithSound, videoItemData, videoSlide } from "../lib/video_slide"

// The lightbox of a gallery grid (`doc/base-ui-seo-pwa-i18n.md` 2.6:
// PhotoSwipe is the one gallery viewer of the stack). Every grid item is an
// ordinary link to the canonical photo, so the grid reads and the photo opens
// without this controller at all; with it, the same link opens in place.
//
// A link marked `data-pswp-type="video"` (the owner library and album of
// phase 055) points at a video rendition instead, and opens as a slide that
// holds a `<video>` with the browser's own controls; PhotoSwipe has no video
// type of its own, so the slide is built through its custom-content events
// (`lib/video_slide.ts`, shared with a post card's `video-card` since full
// phase 093), and a video stops when its slide is left or the lightbox closes.
// The slide the lightbox opens on plays at once when it is a video, inside
// the press's user activation (muted when the browser refuses sound); a slide
// reached by swiping waits for its controls.
//
// A gallery inside a popover (a public pedigree's opened animal, 31.10.2026)
// mounts the lightbox inside that popover: the popover sits in the top layer,
// so a lightbox on the body would open under it. Escape then closes the
// lightbox alone; the popover stays open under it.
//
// Both modules are imported statically: the application bundle is built
// without code splitting (`esbuild.config.mjs`), so a dynamic import would be
// inlined anyway, and the stylesheet comes through the one Tailwind build
// (`app/assets/tailwind/application.css`) rather than through a second CSS
// output beside the bundle.
export default class GalleryController extends Controller<HTMLElement> {
  static targets = ["item"]

  declare readonly itemTargets: HTMLAnchorElement[]

  private lightbox: PhotoSwipeLightbox | null = null
  private opening = false

  connect(): void {
    if (this.itemTargets.length === 0) return

    const popover = this.element.closest<HTMLElement>("[popover]")
    const lightbox = new PhotoSwipeLightbox({
      gallery: this.element,
      children: "a[data-gallery-target='item']",
      pswpModule: PhotoSwipe,
      ...(popover ? { appendToEl: popover } : {}),
      ...lightboxMotion()
    })
    if (popover) {
      lightbox.on("keydown", ({ originalEvent }) => {
        if (originalEvent.key === "Escape") originalEvent.preventDefault()
      })
    }
    lightbox.addFilter("domItemData", (itemData, _element, link) => {
      if (link.dataset.pswpType !== "video") return itemData
      return { ...itemData, ...videoItemData(link) }
    })
    lightbox.on("contentLoad", (event) => {
      const { content } = event
      if (content.type !== "video") return

      event.preventDefault()
      content.element = videoSlide(content.data)
    })
    lightbox.on("beforeOpen", () => {
      this.opening = true
    })
    lightbox.on("contentActivate", ({ content }) => {
      const video = content.type === "video" ? content.element?.querySelector("video") : null
      if (this.opening && video) void playWithSound(video)
      this.opening = false
    })
    lightbox.on("contentDeactivate", ({ content }) => {
      content.element?.querySelector("video")?.pause()
    })
    lightbox.on("close", () => {
      lightbox.pswp?.currSlide?.content.element?.querySelector("video")?.pause()
    })
    lightbox.init()
    this.lightbox = lightbox
  }

  disconnect(): void {
    this.lightbox?.destroy()
    this.lightbox = null
  }
}
